import React, { useEffect, useMemo, useState } from "react";
import { DialogWrapMedium } from "../../../controls/components/dialogWrap/DialogWrap";
import {
	InputOrderAdminTitle,
	UserFormServiceButtonClose,
	UserFormServiceButtonCreated,
} from "../../MyBooking/styled/DesStyled";
import Partner from "../../../../models/Partner";
import useAlertDialog from "../../../hooks/useAlertDialog";
import { AlertType } from "../../../hooks/common/useAttachAlertDialog";
import {
	lockOrUpdatePartnerPayoutLock,
	MIN_PAYOUT_LOCK_REASON,
	unlockPartnerPayoutLock,
} from "../../../../services/PartnerService";

export default function DialogPartnerPayoutLock(props: {
	openDialog: boolean;
	onClose: () => void;
	partner: Partner | null;
	onSuccess?: () => void;
}) {
	const { openAlertDialog } = useAlertDialog();
	const partner = props.partner;

	const title = partner?.payoutLocked ? "Update payout lock reason" : "Lock manual payout requests";

	const initialReason = partner?.payoutLocked ? partner?.payoutLockReason || "" : "";
	const [reason, setReason] = useState(initialReason);

	useEffect(() => {
		if (props.openDialog && partner) {
			setReason(partner.payoutLocked ? partner.payoutLockReason || "" : "");
		}
	}, [props.openDialog, partner]);

	const trimmedLen = useMemo(() => reason.trim().length, [reason]);
	const canSubmit = Boolean(partner?.id) && trimmedLen >= MIN_PAYOUT_LOCK_REASON;

	const handleSubmit = async () => {
		if (!partner?.id || trimmedLen < MIN_PAYOUT_LOCK_REASON) {
			openAlertDialog?.(
				AlertType.Fail,
				`Please enter a detailed reason (at least ${MIN_PAYOUT_LOCK_REASON} characters).`,
				() => {}
			);
			return;
		}
		try {
			await lockOrUpdatePartnerPayoutLock(partner.id, reason.trim());
			openAlertDialog?.(AlertType.Success, partner.payoutLocked ? "Reason updated." : "Payout locked.", () => {
				props.onSuccess?.();
				props.onClose();
			});
		} catch (err: unknown) {
			const msg =
				err && typeof err === "object" && "response" in err
					? (err as { response?: { data?: { message?: string } } }).response?.data?.message
					: undefined;
			openAlertDialog?.(AlertType.Fail, msg || "Request failed.");
		}
	};

	const handleUnlock = () => {
		if (!partner?.id) return;
		openAlertDialog?.(
			AlertType.Confirm,
			"Unlock manual payout requests for this station? The station will be able to request payout again from the station portal.",
			() => {},
			async () => {
				try {
					await unlockPartnerPayoutLock(partner.id);
					openAlertDialog?.(AlertType.Success, "Payout unlocked.", () => {
						props.onSuccess?.();
						props.onClose();
					});
				} catch (err: unknown) {
					const msg =
						err && typeof err === "object" && "response" in err
							? (err as { response?: { data?: { message?: string } } }).response?.data?.message
							: undefined;
					openAlertDialog?.(AlertType.Fail, msg || "Unlock failed.");
				}
			},
			undefined,
			{ declineText: "Cancel", acceptText: "Unlock" }
		);
	};

	if (!partner) return null;

	return (
		<DialogWrapMedium open={props.openDialog} onClose={props.onClose}>
			<div style={{ padding: "8px 4px 0" }}>
				<InputOrderAdminTitle style={{ color: "#b71c1c" }}>{title}</InputOrderAdminTitle>
				<p style={{ fontSize: "13px", color: "#444", margin: "10px 0 12px", lineHeight: 1.5 }}>
					<strong>{partner.name || partner.displayName || `Station #${partner.id}`}</strong>
					<br />
					Partners will <strong>not</strong> be able to submit manual payout requests from the station portal.
					Auto payout rules are unchanged.
				</p>
				<ul
					style={{
						fontSize: "12px",
						color: "#555",
						margin: "0 0 14px",
						paddingLeft: "18px",
						lineHeight: 1.45,
					}}>
					<li>A clear reason is required for audit (min. {MIN_PAYOUT_LOCK_REASON} characters).</li>
					<li>The station will see this reason on their debt / payout screen.</li>
				</ul>
				<label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: 6 }}>
					Reason for operations / compliance
				</label>
				<textarea
					value={reason}
					onChange={(e) => setReason(e.target.value)}
					rows={5}
					style={{
						width: "100%",
						boxSizing: "border-box",
						padding: "10px 12px",
						fontSize: "13px",
						borderRadius: 8,
						border: "1px solid #ccc",
						resize: "vertical",
						fontFamily: "inherit",
					}}
					placeholder="Describe the contract violation or internal reference (visible to the station after lock)."
				/>
				<div style={{ fontSize: "11px", color: trimmedLen >= MIN_PAYOUT_LOCK_REASON ? "#2e7d32" : "#c62828", marginTop: 6 }}>
					{trimmedLen} / {MIN_PAYOUT_LOCK_REASON}+ characters
				</div>
				<div
					style={{
						display: "flex",
						gap: 10,
						justifyContent: partner.payoutLocked ? "space-between" : "flex-end",
						alignItems: "center",
						flexWrap: "wrap",
						marginTop: 18,
					}}>
					{partner.payoutLocked ? (
						<button
							type="button"
							onClick={() => handleUnlock()}
							style={{
								padding: "8px 14px",
								fontSize: "13px",
								fontWeight: 600,
								color: "#1b5e20",
								background: "#e8f5e9",
								border: "1px solid #a5d6a7",
								borderRadius: 8,
								cursor: "pointer",
								fontFamily: "inherit",
							}}>
							Unlock manual payouts
						</button>
					) : null}
					<div style={{ display: "flex", gap: 10 }}>
						<UserFormServiceButtonClose type="button" onClick={props.onClose}>
							Cancel
						</UserFormServiceButtonClose>
						<UserFormServiceButtonCreated type="button" disabled={!canSubmit} onClick={() => void handleSubmit()}>
							{partner.payoutLocked ? "Save reason" : "Confirm lock"}
						</UserFormServiceButtonCreated>
					</div>
				</div>
			</div>
		</DialogWrapMedium>
	);
}
